import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../../services/api';
import { positionAsync, positionOptionLabel } from '../positionPicker';

// Ortak pozisyon seçici tek kaynaktan (../positionPicker) — buradan da dışa aktarılır ki
// mevcut `import { positionAsync } from '.../positions/config'` çağrı yerleri aynen çalışsın.
export { positionAsync, buildPositionAsync, positionOptionLabel } from '../positionPicker';

const usePositionApi = (queryParams) => {
  const { t } = useTranslation();

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const lastValidQueryParams = useRef(null);

  const [departments, setDepartments] = useState([]);
  const [titles, setTitles] = useState([]);
  const [positions, setPositions] = useState([]);

  // Fetch dropdown data on mount
  useEffect(() => {
    api.get('/v1/departments?pageSize=500').then((res) => {
      setDepartments(res.data?.data?.data || []);
    }).catch(() => {});
    api.get('/v1/titles?pageSize=500').then((res) => {
      setTitles(res.data?.data?.data || []);
    }).catch(() => {});
    api.get('/v1/positions?pageSize=500').then((res) => {
      setPositions(res.data?.data?.data || []);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const paramsToUse = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : lastValidQueryParams.current || { pageNumber: 1, pageSize: 10 };
    lastValidQueryParams.current = paramsToUse;
    setFetching(true);
    api.get(`/v1/positions?${new URLSearchParams(paramsToUse)}`)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  // plannedHeadcount is int? on the backend. GenericFormDrawer's plain 'number'
  // field type stores the raw DOM input string (HTML <input> values are always
  // strings, even for type="number"); an unparsed "50" or "" sent as JSON would
  // fail .NET's int? deserialization and reject the whole payload (mirrors the
  // explicit parseInt done for numberOfLicenses in pages/tenants/config/index.js).
  //
  // Pozisyon kodu (2026-09-30): OLUŞTURMADA kod sorulmaz — "Kodu kendim gireceğim"
  // işaretlenmediyse `code` HİÇ gönderilmez ve sunucu sıradaki kodu (POS-0042) atar
  // (PositionDataAccess.ResolveCode). Kutucuk yalnız arayüz durumudur, sunucuya gitmez.
  // Düzenlemede kod alanı her zaman görünür; boş gönderilirse sunucu mevcut kodu korur.
  const preparePayload = (fd, { isCreate } = {}) => {
    const { manualCode, ...rest } = fd;
    const code = isCreate && !manualCode ? undefined : ((rest.code || '').trim() || undefined);
    return {
      ...rest,
      code,
      plannedHeadcount: rest.plannedHeadcount === '' || rest.plannedHeadcount === undefined || rest.plannedHeadcount === null
        ? null
        : parseInt(rest.plannedHeadcount, 10),
    };
  };

  const create = useCallback(async (formData) => {
    setFetching(true);
    try {
      await api.post('/v1/positions', preparePayload(formData, { isCreate: true }));
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      setFetching(false);
      return false;
    }
  }, []);

  const update = useCallback(async (id, formData) => {
    setFetching(true);
    try {
      await api.put(`/v1/positions/${id}`, preparePayload(formData));
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      setFetching(false);
      return false;
    }
  }, []);

  const _delete = useCallback(async (ids) => {
    try {
      if (ids.length === 1) {
        await api.delete(`/v1/positions/${ids[0]}`);
      } else {
        await api.post('/v1/positions/batch-delete', ids);
      }
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      return false;
    }
  }, []);

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    { name: 'code', label: t('position.code'), options: { filter: true, sort: true, filterType: 'textField', filterOptions: { defaultOp: 'like' } } },
    { name: 'titleName', label: t('position.titleName'), options: { filter: true, sort: false, filterType: 'textField', filterOptions: { defaultOp: 'like' } } },
    { name: 'departmentName', label: t('position.departmentName'), options: { filter: true, sort: false, filterType: 'textField', filterOptions: { defaultOp: 'like' } } },
    {
      name: 'parentPositionId', label: t('position.parentPosition'),
      options: {
        filter: false, sort: false,
        customBodyRender: (val, tableMeta) => {
          if (!val) return '-';
          const pos = positions.find(p => p.id === val);
          if (!pos) return data[tableMeta.rowIndex]?.parentPositionCode || '-';
          return positionOptionLabel(pos);
        },
      },
    },
  ], [t, positions, data]);

  // Alan sırası müşterinin düşündüğü sıradır: hangi birim, hangi ünvan, kime bağlı.
  // Kod en sonda ve oluştururken SORULMAZ (bkz. preparePayload).
  const objectSchema = useMemo(() => [
    {
      name: 'departmentId', label: t('position.departmentName'), type: 'select', grid: 12,
      options: departments.map((d) => ({ id: d.id, name: d.name })),
      idKey: 'id', labelKey: 'name',
    },
    {
      name: 'titleId', label: t('position.titleName'), type: 'select', grid: 12,
      options: titles.map((tt) => ({ id: tt.id, name: tt.name })),
      idKey: 'id', labelKey: 'name',
    },
    {
      // Asenkron zengin seçici: ünvan · departman · oturan kişi · kod; kişi adıyla da
      // aranır. Statik 500'lük liste büyük kiracıda üst pozisyonun bir kısmını hiç
      // sunmuyordu.
      name: 'parentPositionId', label: t('position.parentPosition'), type: 'asyncSelect', grid: 12,
      ...positionAsync,
      searchPlaceholder: t('position.pickerSearchPlaceholder'),
    },
    { name: 'plannedHeadcount', label: t('position.plannedHeadcount'), type: 'number', grid: 12, min: 0,
      description: t('position.plannedHeadcountHint') },
    {
      name: 'codeAutoNotice', type: 'notice', grid: 12,
      content: t('position.codeAutoNotice'),
      hidden: (fd) => !!fd.id || !!fd.manualCode,
    },
    {
      name: 'manualCode', label: t('position.codeManualToggle'), type: 'checkbox', grid: 12,
      hidden: (fd) => !!fd.id,
    },
    {
      // Zorunlu DEĞİL: kutucuk işaretli ama alan boş kalırsa sunucu yine kod üretir.
      name: 'code', label: t('position.code'), type: 'text', grid: 12,
      hidden: (fd) => !fd.id && !fd.manualCode,
      description: (fd) => (fd.id ? t('position.codeEditHint') : t('position.codeManualHint')),
    },
  ], [t, departments, titles]);

  return { objectSchema, columns, data, fetching, pagination, create, update, _delete };
};

export default usePositionApi;

// Faz E (spec 2026-08-08 §2-4, E4/Task 5) — GET/PUT v1/positions/{id}/competencies.
// task-3-report.md §1: "Bu uç Task 5'in İKİ tüketicisi için ortak" — pozisyon
// detayının "Yetkinlikler" bölümü (PositionCompetenciesDrawer.js) VE
// FeedbackFormsTab.js'in "pozisyon yetkinliklerinden öner" listesi AYNI bu
// fetch'i kullanır, bu yüzden burada (pozisyon alanının sahibi) export edilip
// diğer sayfaya import edilir (nine-box/config → talent-reports gibi mevcut
// çapraz-sayfa hook paylaşım deseni).
export const fetchPositionCompetencies = async (positionId) => {
  if (!positionId) return [];
  const res = await api.get(`/v1/positions/${positionId}/competencies`);
  return res.data?.data || [];
};

// Pozisyon detayının "Yetkinlikler" satır editörü için: GET ile hydrate,
// PUT tam-liste replace (task-3-report.md §1: dönüş GET ile AYNI şekil —
// "FE optimistic-update round-trip'i için ekstra GET gerekmez").
export const usePositionCompetenciesApi = (positionId) => {
  const [competencies, setCompetencies] = useState([]);
  const [fetching, setFetching] = useState(false);

  useEffect(() => {
    if (!positionId) {
      setCompetencies([]);
      return undefined;
    }
    let cancelled = false;
    setFetching(true);
    fetchPositionCompetencies(positionId)
      .then((rows) => { if (!cancelled) setCompetencies(rows); })
      .catch(() => { if (!cancelled) setCompetencies([]); })
      .finally(() => { if (!cancelled) setFetching(false); });
    return () => { cancelled = true; };
  }, [positionId]);

  const save = useCallback(async (rows) => {
    if (!positionId) return false;
    try {
      const res = await api.put(`/v1/positions/${positionId}/competencies`, {
        competencies: rows.map((r) => ({ competencyId: r.competencyId, targetLevel: r.targetLevel })),
      });
      setCompetencies(res.data?.data || []);
      return true;
    } catch {
      return false;
    }
  }, [positionId]);

  return { competencies, fetching, save };
};
